/** Siempre fijos arriba; no forman parte del layout guardado ni del editor */
export function isMenuFixedTopRef(ref) {
  return ref === "nav:dashboard" || ref === "nav:settings";
}

/** Siempre fijo al final del menú (si hay acceso); tampoco entra en el layout guardado */
export function isMenuFixedBottomRef(ref) {
  return ref === "nav:support";
}

export function isDisabledBuiltinRef(ref) {
  return ref === "nav:fobtrading";
}

/**
 * Quita del árbol guardado las entradas model:* cuyo modelo tiene hideFromMenu
 * (p. ej. layout de menú anterior a ocultar el modelo).
 * @param {Array} roots nodos { t:'b'|'f', r?, id?, c?, l? }
 * @param {Array<{ modelName?: string, hideFromMenu?: boolean }>} models
 */
export function stripHiddenModelsFromRawRoots(roots, models) {
  if (!Array.isArray(roots)) return [];
  const list = Array.isArray(models) ? models : [];
  const byName = new Map();
  for (const m of list) {
    if (m && m.modelName) {
      byName.set(String(m.modelName).toLowerCase(), m);
    }
  }
  const walk = (arr) => {
    if (!Array.isArray(arr)) return [];
    const out = [];
    for (const n of arr) {
      if (!n) continue;
      if (n.t === "u" && n.u != null && String(n.u).trim() && n.l != null) {
        const o = {
          t: "u",
          u: String(n.u).trim().slice(0, 2048),
          l: String(n.l).trim().slice(0, 120),
        };
        const cid =
          n.cid != null && String(n.cid).trim()
            ? String(n.cid).trim().slice(0, 64)
            : "";
        if (cid) o.cid = cid;
        if (n.i != null && String(n.i).trim())
          o.i = String(n.i).trim().slice(0, 40);
        out.push(o);
        continue;
      }
      if (n.t === "b" && n.r && String(n.r).startsWith("model:")) {
        const mn = String(n.r).slice(6);
        const ent = byName.get(mn.toLowerCase());
        if (ent && ent.hideFromMenu) continue;
        const inner = walk(n.c || []);
        const o = { t: "b", r: n.r };
        if (n.l != null && String(n.l).trim())
          o.l = String(n.l).trim().slice(0, 120);
        if (n.h === 1 || n.h === true) o.h = 1;
        if (inner.length) o.c = inner;
        out.push(o);
        continue;
      }
      if (n.t === "f" && n.id != null) {
        const inner = walk(n.c || []);
        out.push({ t: "f", id: String(n.id), c: inner });
        continue;
      }
      if (n.t === "b" && n.r) {
        const inner = walk(n.c || []);
        const o = { t: "b", r: n.r };
        if (n.l != null && String(n.l).trim())
          o.l = String(n.l).trim().slice(0, 120);
        if (inner.length) o.c = inner;
        out.push(o);
        continue;
      }
    }
    return out;
  };
  return walk(roots);
}

/**
 * Lista plana de refs en orden de recorrido en profundidad (modelo y luego sus reportes en c).
 */
export function flattenBuiltinRefsFromRoots(roots) {
  const out = [];
  const walk = (nodes) => {
    if (!Array.isArray(nodes)) return;
    for (const n of nodes) {
      if (!n) continue;
      if (n.t === "b" && n.r) {
        out.push(n.r);
        if (n.c && n.c.length) walk(n.c);
      } else if (n.t === "f" && n.c && n.c.length) {
        walk(n.c);
      }
    }
  };
  walk(roots);
  return out;
}

/**
 * Reportes que cuelgan de un modelo en el menú: los del modelo y los de sus
 * grupos (`Contrato_POSICIONES`), que no tienen entrada propia de modelo y si
 * no quedaban afuera del menú. Los de un grupo piden permiso de ver ese grupo.
 * @param {Array} reports
 * @param {string} modelName
 * @param {(groupModelLower: string) => boolean} [canViewGroup]
 */
export function reportsOfModel(reports, modelName, canViewGroup) {
  const base = String(modelName || "").toLowerCase();
  if (!base) return [];
  return (reports || []).filter((report) => {
    const name = String((report && report.modelName) || "").toLowerCase();
    if (name === base) return true;
    return (
      name.startsWith(`${base}_`) && (!canViewGroup || canViewGroup(name))
    );
  });
}

/**
 * Árbol por defecto: cada reporte va en model.c (submenú del modelo).
 * ctx: filteredModels, getModelReports(modelName), flags canAccess*
 */
export function buildDefaultMenuRawRoots(ctx) {
  const roots = [];
  if (ctx.canAccessStockModule) {
    roots.push({ t: "b", r: "nav:stock" });
    roots.push({ t: "b", r: "nav:containers" });
  }
  for (const model of ctx.filteredModels || []) {
    const reps =
      (ctx.getModelReports && ctx.getModelReports(model.modelName)) || [];
    const reportNodes = reps
      .filter((r) => r && r._id)
      .map((r) => ({ t: "b", r: `report:${r._id}` }));
    const node = { t: "b", r: `model:${model.modelName}` };
    if (reportNodes.length) {
      node.c = reportNodes;
    }
    roots.push(node);
  }
  if (ctx.canAccessFinancialModuleWithPermissions) {
    roots.push({ t: "b", r: "nav:financial" });
  }
  if (ctx.canAccessAccountingModule) {
    roots.push({ t: "b", r: "nav:accounting" });
  }
  if (ctx.canAccessDHLModule) {
    roots.push({ t: "b", r: "nav:dhl" });
  }
  if (ctx.canAccessFindTeuModule) {
    roots.push({ t: "b", r: "nav:ft_container" });
  }
  if (ctx.canAccessComexstatsModule) {
    roots.push({ t: "b", r: "nav:comexstats" });
  }
  if (ctx.canAccessHistoryModule) {
    roots.push({ t: "b", r: "nav:history" });
  }
  if (ctx.canAccessMevModule) {
    roots.push({ t: "b", r: "nav:mev" });
  }
  if (ctx.canAccessOutlookModule) {
    roots.push({ t: "b", r: "nav:outlook" });
  }
  if (ctx.canAccessNotaComercialModule) {
    roots.push({ t: "b", r: "nav:nota_comercial" });
  }
  if (ctx.canAccessAlisofModule) {
    roots.push({ t: "b", r: "nav:alisof" });
    roots.push({ t: "b", r: "nav:alisof_posiciones" });
  }
  if (ctx.canAccessBolsaComercialesModule) {
    roots.push({ t: "b", r: "nav:bolsa_comerciales" });
  }
  if (ctx.canAccessFacturacionAbogadosModule) {
    roots.push({ t: "b", r: "nav:facturacion_abogados" });
  }
  if (ctx.canAccessJurisprudenciaModule) {
    roots.push({ t: "b", r: "nav:jurisprudencia" });
  }
  if (ctx.canAccessArcaModule) {
    roots.push({ t: "b", r: "nav:arca" });
  }
  if (ctx.canAccessAgentChat) {
    roots.push({ t: "b", r: "nav:agent_chat" });
  }
  if (ctx.canAccessInmobiliariaModule) {
    roots.push({ t: "b", r: "nav:inmobiliaria" });
  }
  if (ctx.canAccessExcelMappingModule) {
    roots.push({ t: "b", r: "nav:excel_mapping" });
  }
  if (ctx.canAccessWebsiteModule) {
    roots.push({ t: "b", r: "nav:websites" });
  }
  if (ctx.canAccessEmailModule) {
    roots.push({ t: "b", r: "nav:emails" });
  }
  if (ctx.canAccessTranslationsModule) {
    roots.push({ t: "b", r: "nav:translations" });
  }
  if (ctx.canAccessInstanceBuilderModule) {
    roots.push({ t: "b", r: "nav:instance_builder" });
  }
  if (ctx.canAccessTelegramAiModule) {
  }
  if (ctx.isChatModuleActive) {
    roots.push({ t: "b", r: "nav:chat" });
  }
  if (ctx.isCronjobsModuleActive && ctx.isAdminRole) {
    roots.push({ t: "b", r: "nav:cronjobs" });
  }
  if (ctx.canAccessSettingsSystemConfig) {
    roots.push({ t: "b", r: "settings:system_config" });
  }
  if (ctx.canAccessSettingsModelReferenceMapping) {
    roots.push({ t: "b", r: "settings:model_reference_mapping" });
  }
  if (ctx.canAccessSettingsModelDefinitions) {
    roots.push({ t: "b", r: "settings:model_definitions" });
  }
  if (ctx.canAccessSettingsHookAutomations) {
    roots.push({ t: "b", r: "settings:hook_automations" });
  }
  if (ctx.canAccessSettingsViews) {
    roots.push({ t: "b", r: "settings:views" });
  }
  if (ctx.canAccessSettingsRoles) {
    roots.push({ t: "b", r: "settings:roles" });
  }
  if (ctx.canAccessSettingsUsers) {
    roots.push({ t: "b", r: "settings:users" });
  }
  if (ctx.canAccessSettingsLists) {
    roots.push({ t: "b", r: "settings:lists" });
  }
  if (ctx.canAccessSettingsAdminLinks) {
    roots.push({ t: "b", r: "settings:admin_links" });
  }
  if (ctx.canAccessSettingsModules) {
    roots.push({ t: "b", r: "settings:modules" });
  }
  if (ctx.canAccessSettingsRelationships) {
    roots.push({ t: "b", r: "settings:relationships" });
  }
  if (ctx.canAccessSettingsPromptSettings) {
    roots.push({ t: "b", r: "settings:prompt_settings" });
  }
  if (ctx.canAccessSettingsOrphans) {
    roots.push({ t: "b", r: "settings:orphans" });
  }
  return roots;
}

/** Quita dashboard/settings/soporte del árbol (raíz y dentro de carpetas / bajo modelos) */
export function stripFixedRefsFromRawRoots(roots) {
  if (!Array.isArray(roots)) return [];
  const out = [];
  for (const n of roots) {
    if (!n) continue;
    if (
      n.t === "b" &&
      n.r &&
      (isMenuFixedTopRef(n.r) ||
        isMenuFixedBottomRef(n.r) ||
        isDisabledBuiltinRef(n.r))
    )
      continue;
    if (n.t === "u" && n.u != null && String(n.u).trim() && n.l != null) {
      const o = {
        t: "u",
        u: String(n.u).trim().slice(0, 2048),
        l: String(n.l).trim().slice(0, 120),
      };
      const cid =
        n.cid != null && String(n.cid).trim()
          ? String(n.cid).trim().slice(0, 64)
          : "";
      if (cid) o.cid = cid;
      if (n.i != null && String(n.i).trim())
        o.i = String(n.i).trim().slice(0, 40);
      out.push(o);
      continue;
    }
    if (n.t === "b" && n.r) {
      if (n.r.startsWith("model:")) {
        const inner = stripFixedRefsFromRawRoots(n.c || []);
        const o = { t: "b", r: n.r };
        if (n.l != null && String(n.l).trim()) {
          o.l = String(n.l).trim().slice(0, 120);
        }
        if (n.h === 1 || n.h === true) o.h = 1;
        if (inner.length) o.c = inner;
        out.push(o);
      } else {
        const o = { t: "b", r: n.r };
        if (n.l != null && String(n.l).trim()) {
          o.l = String(n.l).trim().slice(0, 120);
        }
        out.push(o);
      }
      continue;
    }
    if (n.t === "f" && n.id != null) {
      out.push({
        t: "f",
        id: String(n.id),
        c: stripFixedRefsFromRawRoots(n.c || []),
      });
    }
  }
  return out;
}

/**
 * Normaliza el árbol guardado (sin dashboard/settings) y, solo si queda vacío,
 * usa el árbol por defecto del sistema. No reinyecta enlaces quitados por el usuario.
 * @param {Array} roots nodos { t:'b'|'f', r?, id?, c? }
 * @param {Array|null} defaultRawRoots árbol por defecto (p. ej. reportes bajo model); si roots vacío tras strip, se usa como base
 */
export function mergeMissingBuiltins(roots, defaultRawRoots = null) {
  let out = stripFixedRefsFromRawRoots(
    Array.isArray(roots) ? JSON.parse(JSON.stringify(roots)) : []
  );
  if (
    out.length === 0 &&
    defaultRawRoots &&
    Array.isArray(defaultRawRoots) &&
    defaultRawRoots.length
  ) {
    out = JSON.parse(JSON.stringify(defaultRawRoots));
  }
  return out;
}

/**
 * Referencias builtin (nav/model/report) del menú por defecto que aún no están en roots.
 * @param {Array} roots nodos raw
 * @param {string[]} defaultBuiltinRefs orden de referencia (p. ej. flatten del menú por defecto)
 * @returns {string[]}
 */
export function listMissingBuiltinRefs(roots, defaultBuiltinRefs) {
  const seen = new Set();
  function walk(arr) {
    if (!Array.isArray(arr)) return;
    for (const n of arr) {
      if (n && n.t === "b" && n.r) seen.add(n.r);
      if (n && n.t === "b" && Array.isArray(n.c)) walk(n.c);
      if (n && n.t === "f" && Array.isArray(n.c)) walk(n.c);
    }
  }
  walk(Array.isArray(roots) ? roots : []);
  const out = [];
  for (const r of defaultBuiltinRefs || []) {
    if (isMenuFixedTopRef(r) || isMenuFixedBottomRef(r)) continue;
    if (!seen.has(r)) out.push(r);
  }
  return out;
}

/**
 * Añade al final nodos sueltos { t:'b', r } por cada ref en defaultBuiltinRefs
 * que aún no aparece en el árbol (recorrido en profundidad). Para incorporar
 * módulos o reportes nuevos sin perder el orden guardado.
 * @param {Array} roots nodos raw
 * @param {string[]} defaultBuiltinRefs orden de referencia (p. ej. flatten del menú por defecto)
 */
export function appendMissingBuiltinRefsToRawRoots(roots, defaultBuiltinRefs) {
  const out = Array.isArray(roots) ? JSON.parse(JSON.stringify(roots)) : [];
  for (const r of listMissingBuiltinRefs(out, defaultBuiltinRefs)) {
    out.push({ t: "b", r });
  }
  return out;
}

/**
 * Asegura que reportes existentes en el sistema se vean en el menú por defecto.
 * Inserta report:* faltantes dentro del nodo model:* correspondiente (sin duplicar).
 * @param {Array} roots árbol raw guardado/mezclado del usuario
 * @param {Array<{_id?: string, modelName?: string}>} reports reportes disponibles
 * @param {Array<{modelName?: string}>} filteredModels modelos visibles para el usuario
 */
export function appendMissingReportsToModelNodes(
  roots,
  reports,
  filteredModels = []
) {
  const out = Array.isArray(roots) ? JSON.parse(JSON.stringify(roots)) : [];
  if (!Array.isArray(reports) || !reports.length) return out;

  const allowedModelNames = new Set(
    (Array.isArray(filteredModels) ? filteredModels : [])
      .map((m) => (m && m.modelName ? String(m.modelName).toLowerCase() : ""))
      .filter(Boolean)
  );

  const seenReportRefs = new Set();
  const modelNodesByName = new Map();

  const walk = (nodes) => {
    if (!Array.isArray(nodes)) return;
    for (const n of nodes) {
      if (!n || (n.t !== "b" && n.t !== "u") || (n.t === "b" && !n.r)) {
        if (n && n.t === "f" && Array.isArray(n.c)) walk(n.c);
        continue;
      }
      if (n.t === "u") {
        continue;
      }
      if (String(n.r).startsWith("report:")) {
        seenReportRefs.add(String(n.r));
      }
      if (String(n.r).startsWith("model:")) {
        const modelName = String(n.r).slice(6).toLowerCase();
        if (!modelNodesByName.has(modelName))
          modelNodesByName.set(modelName, []);
        modelNodesByName.get(modelName).push(n);
      }
      if (Array.isArray(n.c)) walk(n.c);
    }
  };
  walk(out);

  for (const rep of reports) {
    if (!rep || !rep._id || !rep.modelName) continue;
    const modelName = String(rep.modelName).toLowerCase();
    if (allowedModelNames.size && !allowedModelNames.has(modelName)) continue;
    const reportRef = `report:${rep._id}`;
    if (seenReportRefs.has(reportRef)) continue;

    const modelNodes = modelNodesByName.get(modelName);
    if (!Array.isArray(modelNodes) || modelNodes.length === 0) continue;

    const targetModelNode = modelNodes[0];
    if (!Array.isArray(targetModelNode.c)) targetModelNode.c = [];
    targetModelNode.c.push({ t: "b", r: reportRef });
    seenReportRefs.add(reportRef);
  }

  return out;
}

/** Serializa el árbol del editor (objetos con _key) al formato guardado en API */
export function editorNodesToRawRoots(nodes) {
  if (!Array.isArray(nodes)) return [];
  const out = nodes.map((n) => editorNodeToRaw(n)).filter(Boolean);
  const hasSettings = flattenBuiltinRefsFromRoots(out).some(
    (r) => typeof r === "string" && r.startsWith("settings:")
  );
  if (!hasSettings) {
    out.push({ t: "b", r: "settings:none" });
  }
  return out;
}

function editorNodeToRaw(n) {
  if (!n || !n.type) return null;
  if (n.type === "builtin") {
    if (
      !n.ref ||
      isMenuFixedTopRef(n.ref) ||
      isMenuFixedBottomRef(n.ref) ||
      isDisabledBuiltinRef(n.ref) ||
      n.ref === "settings:user_menu"
    ) {
      return null;
    }
    const rawKids = Array.isArray(n.children)
      ? n.children.map(editorNodeToRaw).filter(Boolean)
      : [];
    const o = { t: "b", r: n.ref };
    const canCustomLabel =
      n.ref.startsWith("model:") ||
      (n.ref.startsWith("nav:") && !isMenuFixedTopRef(n.ref)) ||
      n.ref.startsWith("settings:");
    if (canCustomLabel) {
      const lab =
        n.menuLabel != null ? String(n.menuLabel).trim().slice(0, 120) : "";
      if (lab) o.l = lab;
    }
    if (n.ref.startsWith("model:") && n.hideAddNew) {
      o.h = 1;
    }
    if (n.ref.startsWith("model:") && rawKids.length) {
      o.c = rawKids;
    }
    return o;
  }
  if (n.type === "customLink") {
    const u = n.href != null ? String(n.href).trim().slice(0, 2048) : "";
    const l = n.label != null ? String(n.label).trim().slice(0, 120) : "";
    if (!u || !l) return null;
    const o = { t: "u", u, l };
    const cid =
      n.cid != null && String(n.cid).trim()
        ? String(n.cid).trim().slice(0, 64)
        : "";
    if (cid) o.cid = cid;
    const ic = n.icon != null ? String(n.icon).trim().slice(0, 40) : "";
    if (ic) o.i = ic;
    return o;
  }
  if (n.type === "folder") {
    const id = n.folderId != null ? String(n.folderId) : "";
    if (!id) return null;
    const c = Array.isArray(n.children)
      ? n.children.map(editorNodeToRaw).filter(Boolean)
      : [];
    return { t: "f", id, c };
  }
  return null;
}

export function rawRootsToEditorNodes(roots, folders) {
  const folderMap = new Map((folders || []).map((f) => [String(f._id), f]));
  function mapNode(n) {
    if (!n) return null;
    if (n.t === "b" && n.r) {
      if (
        isMenuFixedTopRef(n.r) ||
        isMenuFixedBottomRef(n.r) ||
        isDisabledBuiltinRef(n.r) ||
        n.r === "settings:user_menu" ||
        n.r === "settings:none"
      ) {
        return null;
      }
      const mappedKids = (Array.isArray(n.c) ? n.c : [])
        .map(mapNode)
        .filter(Boolean);
      const o = {
        _key: `b:${n.r}`,
        type: "builtin",
        ref: n.r,
      };
      const canCustomLabel =
        n.r.startsWith("model:") ||
        (n.r.startsWith("nav:") && !isMenuFixedTopRef(n.r)) ||
        n.r.startsWith("settings:");
      if (canCustomLabel) {
        o.menuLabel =
          n.l != null && String(n.l).trim()
            ? String(n.l).trim().slice(0, 120)
            : "";
      }
      if (n.r.startsWith("model:")) {
        o.children = mappedKids;
        o.hideAddNew = n.h === 1 || n.h === true;
      }
      return o;
    }
    if (n.t === "u" && n.u != null && String(n.u).trim() && n.l != null) {
      let cid =
        n.cid != null && String(n.cid).trim()
          ? String(n.cid).trim().slice(0, 64)
          : "";
      if (
        !cid &&
        typeof globalThis !== "undefined" &&
        globalThis.crypto &&
        typeof globalThis.crypto.randomUUID === "function"
      ) {
        cid = globalThis.crypto.randomUUID();
      } else if (!cid) {
        cid = `u_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
      }
      return {
        _key: `u:${cid}`,
        type: "customLink",
        cid,
        href: String(n.u).trim().slice(0, 2048),
        label: String(n.l).trim().slice(0, 120),
        icon:
          n.i != null && String(n.i).trim()
            ? String(n.i).trim().slice(0, 40)
            : "",
      };
    }
    if (n.t === "f" && n.id) {
      const f = folderMap.get(String(n.id));
      if (!f) return null;
      const children = (n.c || []).map(mapNode).filter(Boolean);
      return {
        _key: `f:${n.id}`,
        type: "folder",
        folderId: String(n.id),
        label: f.label,
        icon: f.icon || "folder",
        children,
      };
    }
    return null;
  }
  return (roots || []).map(mapNode).filter(Boolean);
}
